import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useSmartBack } from '../../hooks/useSmartBack';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import UserAvatar from '../../components/UserAvatar';
import {
  ArrowLeft, Loader2, Search, CreditCard, Send, ExternalLink, Copy, Trash2,
  Check, Clock, AlertCircle, TrendingUp, CalendarClock, X
} from 'lucide-react';
import { toast } from 'sonner';
import { AdminSubNav, SUBNAV_INVOICES } from '../../components/admin/AdminSubNav';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: 'Pending',  color: 'bg-amber-50 text-amber-700 border-amber-200',     icon: Clock },
  used:    { label: 'Used',     color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: Check },
  expired: { label: 'Expired',  color: 'bg-zinc-100 text-zinc-500 border-zinc-200',       icon: AlertCircle },
};

const FILTERS = ['all', 'pending', 'used', 'expired'];

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const fmtRel = (iso) => {
  if (!iso) return '—';
  const diff = (new Date(iso).getTime() - Date.now()) / 1000;
  const abs = Math.abs(diff);
  const future = diff > 0;
  if (abs < 60) return future ? 'in a moment' : 'just now';
  if (abs < 3600) return `${Math.round(abs / 60)}m ${future ? 'left' : 'ago'}`;
  if (abs < 86400) return `${Math.round(abs / 3600)}h ${future ? 'left' : 'ago'}`;
  return `${Math.round(abs / 86400)}d ${future ? 'left' : 'ago'}`;
};

const AdminSentLinksPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { back } = useSmartBack('/admin/dashboard');
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [actionId, setActionId] = useState('');
  const [resendResult, setResendResult] = useState(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (status !== 'all') params.status = status;
      if (search.trim()) params.search = search.trim();
      const [list, st] = await Promise.all([
        axios.get(`${API_URL}/api/admin/card-capture`, { headers, params }),
        axios.get(`${API_URL}/api/admin/card-capture/stats`, { headers }),
      ]);
      setRows(list.data);
      setStats(st.data);
    } catch { toast.error('Failed to load sent links'); }
    finally { setLoading(false); }
  }, [headers, status, search]);

  useEffect(() => { load(); }, [load]);

  const resend = async (row) => {
    setActionId(`resend-${row.id}`);
    try {
      const res = await axios.post(`${API_URL}/api/admin/card-capture/${row.id}/resend`, {}, { headers });
      const msg = res.data.regenerated_token
        ? 'Fresh link generated and emailed'
        : res.data.sent_email ? 'Re-sent to client'
        : 'Link refreshed — email send failed, copy the URL';
      toast.success(msg);
      setResendResult({ ...res.data, client_name: row.client_name, client_email: row.client_email });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to resend'); }
    finally { setActionId(''); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete card-save link for ${row.client_email}? This won't affect any card already saved.`)) return;
    setActionId(`del-${row.id}`);
    try {
      await axios.delete(`${API_URL}/api/admin/card-capture/${row.id}`, { headers });
      toast.success('Link deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setActionId(''); }
  };

  const cancelSchedule = async (row) => {
    if (!window.confirm(`Cancel the scheduled email for ${row.client_email}? The link stays valid for manual sending.`)) return;
    setActionId(`cancel-${row.id}`);
    try {
      await axios.post(`${API_URL}/api/admin/card-capture/${row.id}/cancel-schedule`, {}, { headers });
      toast.success('Scheduled send cancelled');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setActionId(''); }
  };

  const copyUrl = async (row) => {
    const url = row.capture_origin
      ? `${row.capture_origin}/card-capture/${row.token}`
      : `${window.location.origin}/card-capture/${row.token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy');
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50">
      <Loader2 className="h-8 w-8 animate-spin text-[#F56F05]" />
    </div>
  );

  const statCards = [
    { label: 'Total Sent', value: stats?.total || 0, icon: Send,        accent: 'text-zinc-900', tint: 'bg-zinc-100' },
    { label: 'Pending',    value: stats?.pending || 0, icon: Clock,       accent: 'text-amber-600', tint: 'bg-amber-50' },
    { label: 'Cards Saved', value: stats?.used || 0,    icon: Check,       accent: 'text-emerald-600', tint: 'bg-emerald-50' },
    { label: 'Conversion', value: `${stats?.conversion_rate || 0}%`, icon: TrendingUp, accent: 'text-[#F56F05]', tint: 'bg-orange-50' },
  ];

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-sent-links-page">
      <header className="bg-white border-b border-zinc-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={back} data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-zinc-900">Sent Card Links</h1>
              <p className="text-xs text-zinc-500">{rows.length} link{rows.length === 1 ? '' : 's'} · sent via /admin/customers → "Send Card Link"</p>
            </div>
          </div>
          <Button onClick={() => navigate('/admin/customers')} className="bg-[#F56F05] hover:bg-[#e06505] text-white rounded-xl" data-testid="open-customers">
            <CreditCard className="h-4 w-4 mr-2" /> Send New Link
          </Button>
        </div>
      </header>
      <AdminSubNav section="Billing" items={SUBNAV_INVOICES} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {statCards.map(s => (
            <Card key={s.label} className="border-zinc-100" data-testid={`stat-${s.label.toLowerCase().replace(/ /g,'-')}`}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl ${s.tint} flex items-center justify-center`}>
                  <s.icon className={`h-5 w-5 ${s.accent}`} />
                </div>
                <div>
                  <p className="text-xs text-zinc-500">{s.label}</p>
                  <p className={`text-lg font-bold ${s.accent}`}>{s.value}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by client name, email or sender..."
              className="pl-9 rounded-xl border-zinc-200" data-testid="search-input" />
          </div>
          <div className="flex gap-2 flex-wrap">
            {FILTERS.map(s => (
              <Button key={s} variant={status === s ? 'default' : 'outline'} size="sm"
                className={`rounded-full text-xs capitalize ${status === s ? 'bg-zinc-900 text-white' : 'border-zinc-200'}`}
                onClick={() => setStatus(s)} data-testid={`filter-${s}`}>
                {s === 'all' ? 'All' : STATUS_CONFIG[s]?.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Table */}
        {rows.length === 0 ? (
          <Card className="border-zinc-100 border-dashed">
            <CardContent className="py-16 text-center">
              <Send className="h-10 w-10 text-zinc-300 mx-auto mb-3" />
              <h3 className="font-bold text-zinc-900 mb-1">No card-save links sent yet</h3>
              <p className="text-sm text-zinc-500 mb-4">From any customer profile, click "Send Card Link" to email a secure save-card link.</p>
              <Button onClick={() => navigate('/admin/customers')} className="bg-[#F56F05] hover:bg-[#e06505] text-white rounded-xl">
                <CreditCard className="h-4 w-4 mr-2" /> Browse Customers
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-zinc-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="sent-links-table">
                <thead>
                  <tr className="border-b border-zinc-100 bg-zinc-50/50">
                    <th className="text-left text-xs font-semibold text-zinc-500 px-4 py-3">Client</th>
                    <th className="text-left text-xs font-semibold text-zinc-500 px-4 py-3 hidden md:table-cell">Sent By</th>
                    <th className="text-left text-xs font-semibold text-zinc-500 px-4 py-3 hidden lg:table-cell">Sent</th>
                    <th className="text-left text-xs font-semibold text-zinc-500 px-4 py-3 hidden lg:table-cell">Expires</th>
                    <th className="text-center text-xs font-semibold text-zinc-500 px-4 py-3">Status</th>
                    <th className="text-right text-xs font-semibold text-zinc-500 px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => {
                    const cfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.pending;
                    const StatusIcon = cfg.icon;
                    return (
                      <tr key={r.id} className="border-b border-zinc-50 hover:bg-zinc-50/50 transition-colors" data-testid={`sent-link-row-${r.id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <UserAvatar user={{ full_name: r.client_name, email: r.client_email, role: 'client' }} size="sm" />
                            <div className="min-w-0">
                              <p className="font-semibold text-sm text-zinc-900 truncate">{r.client_name}</p>
                              <p className="text-[11px] text-zinc-400 truncate">{r.client_email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <p className="text-sm text-zinc-700">{r.created_by_name || '—'}</p>
                          {r.resend_count > 0 && <p className="text-[10px] text-zinc-400">Resent {r.resend_count}×</p>}
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell text-xs text-zinc-500">{fmtDate(r.created_at)}</td>
                        <td className="px-4 py-3 hidden lg:table-cell text-xs text-zinc-500">
                          {r.status === 'used'
                            ? <span className="text-emerald-600">used {fmtRel(r.used_at)}</span>
                            : r.status === 'scheduled' && r.scheduled_send_at
                              ? <span className="text-orange-600">sends {fmtRel(r.scheduled_send_at)}</span>
                              : <span>{fmtDate(r.expires_at)} <span className="text-zinc-400">· {fmtRel(r.expires_at)}</span></span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge className={`${cfg.color} border text-[10px] font-semibold inline-flex items-center gap-1`} data-testid={`status-${r.id}`}>
                            <StatusIcon className="h-3 w-3" /> {cfg.label}
                          </Badge>
                          {r.status === 'scheduled' && r.scheduled_send_at && (
                            <p className="mt-1 text-[10px] text-orange-600 font-semibold" data-testid={`scheduled-at-${r.id}`}>
                              {new Date(r.scheduled_send_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {r.status !== 'used' && (
                              <Button variant="ghost" size="icon" className="h-8 w-8" title="Copy URL"
                                onClick={() => copyUrl(r)} data-testid={`copy-${r.id}`}>
                                <Copy className="h-3.5 w-3.5 text-zinc-400" />
                              </Button>
                            )}
                            {r.status === 'scheduled' && (
                              <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-red-50" title="Cancel scheduled send"
                                onClick={() => cancelSchedule(r)} disabled={actionId === `cancel-${r.id}`} data-testid={`cancel-schedule-${r.id}`}>
                                {actionId === `cancel-${r.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5 text-red-500" />}
                              </Button>
                            )}
                            {r.status !== 'used' && r.status !== 'scheduled' && (
                              <Button variant="ghost" size="icon" className="h-8 w-8" title="Resend email"
                                onClick={() => resend(r)} disabled={actionId === `resend-${r.id}`} data-testid={`resend-${r.id}`}>
                                {actionId === `resend-${r.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5 text-[#F56F05]" />}
                              </Button>
                            )}
                            <Button variant="ghost" size="icon" className="h-8 w-8" title="Delete"
                              onClick={() => remove(r)} disabled={actionId === `del-${r.id}`} data-testid={`del-${r.id}`}>
                              {actionId === `del-${r.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 text-zinc-400 hover:text-red-500" />}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {/* Resend Result Dialog */}
      <Dialog open={!!resendResult} onOpenChange={(o) => !o && setResendResult(null)}>
        <DialogContent className="sm:max-w-md" data-testid="resend-result-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-4 w-4 text-[#F56F05]" /> Link {resendResult?.regenerated_token ? 'Regenerated' : 'Resent'}
            </DialogTitle>
          </DialogHeader>
          {resendResult && (
            <div className="space-y-3 pt-2">
              <div className="bg-zinc-50 rounded-xl p-3 text-sm">
                <p className="font-semibold text-zinc-900">{resendResult.client_name}</p>
                <p className="text-xs text-zinc-500">{resendResult.client_email}</p>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-start gap-2">
                <Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                <div className="text-xs text-emerald-800">
                  <p className="font-semibold">{resendResult.sent_email ? 'Email sent' : 'Link refreshed'}</p>
                  <p className="opacity-80">
                    {resendResult.regenerated_token
                      ? 'A fresh single-use token was generated and emailed.'
                      : resendResult.sent_email
                        ? 'Your client just received a fresh nudge.'
                        : 'Copy the URL below and send it manually — the email service was unreachable.'}
                  </p>
                </div>
              </div>
              {resendResult.capture_url && (
                <div>
                  <p className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1">Capture URL</p>
                  <div className="flex gap-2">
                    <input readOnly value={resendResult.capture_url}
                      className="flex-1 rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-700"
                      data-testid="resend-result-url" />
                    <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(resendResult.capture_url); toast.success('Copied'); }} className="rounded-xl">
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              )}
              <Button onClick={() => setResendResult(null)} className="w-full bg-zinc-900 hover:bg-zinc-800 text-white rounded-xl">Done</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSentLinksPage;
